import React, { useEffect, useState } from 'react';
import { newsAPI } from '../services/websiteAPI';
import ConfirmModal from '../components/ConfirmModal';
import api from '../services/api.jsx';

const News = () => {
  const [news, setNews] = useState([]);
  const [rssFeeds, setRssFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRssForm, setShowRssForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null });
  const [confirmDeleteRss, setConfirmDeleteRss] = useState({ show: false, id: null });
  const [newRssUrl, setNewRssUrl] = useState('');
  const [newRssFeedName, setNewRssFeedName] = useState('');
  const [addingRss, setAddingRss] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    pubDate: '',
    description: '',
    link: ''
  });

  useEffect(() => {
    loadNews();
    loadRssFeeds();
  }, []);

  const loadNews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/news/with-rss');
      setNews(response.data);
    } catch (err) {
      console.error('Failed to load news', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRssFeeds = async () => {
    try {
      const response = await api.get('/news/feeds');
      setRssFeeds(response.data);
    } catch (err) {
      console.error('Failed to load RSS feeds', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await newsAPI.update(editingId, formData);
      } else {
        await newsAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', pubDate: '', description: '', link: '' });
      await loadNews();
    } catch (err) {
      alert(`Failed to ${editingId ? 'update' : 'create'} news article: ` + err.response?.data?.error);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await newsAPI.getById(id);
      const article = response.data;
      setFormData({
        title: article.Title,
        pubDate: article.PubDate ? article.PubDate.split('T')[0] : '',
        description: article.Description || '',
        link: article.Link || ''
      });
      setEditingId(id);
      setShowForm(true);
    } catch (err) {
      alert('Failed to load news article for editing');
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', pubDate: '', description: '', link: '' });
  };

  const handleDelete = async () => {
    try {
      await newsAPI.delete(confirmDelete.id);
      await loadNews();
      setConfirmDelete({ show: false, id: null });
    } catch (err) {
      alert('Failed to delete news article');
      setConfirmDelete({ show: false, id: null });
    }
  };

  const handleAddRssFeed = async (e) => {
    e.preventDefault();
    if (!newRssUrl.trim()) {
      alert('Please enter an RSS feed URL');
      return;
    }

    try {
      setAddingRss(true);
      await api.post('/news/feeds', {
        feedURL: newRssUrl,
        feedName: newRssFeedName || null
      });
      setNewRssUrl('');
      setNewRssFeedName('');
      setShowRssForm(false);
      await loadRssFeeds();
      await loadNews();
    } catch (err) {
      alert('Failed to add RSS feed: ' + (err.response?.data?.error || err.message));
    } finally {
      setAddingRss(false);
    }
  };

  const handleDeleteRssFeed = async () => {
    try {
      await api.delete(`/news/feeds/${confirmDeleteRss.id}`);
      await loadRssFeeds();
      await loadNews();
      setConfirmDeleteRss({ show: false, id: null });
    } catch (err) {
      alert('Failed to delete RSS feed');
      setConfirmDeleteRss({ show: false, id: null });
    }
  };

  if (loading) return <div className="loading">Loading news...</div>;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>News Articles</h1>
        <button onClick={() => editingId ? handleCancelEdit() : setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Cancel' : 'Add News Article'}
        </button>
      </div>

      {/* RSS Feeds Management Section */}
      <div className="card" style={{ marginBottom: '20px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: '0 0 0 0' }}>RSS Feeds</h3>
          <button 
            onClick={() => setShowRssForm(!showRssForm)} 
            className="btn btn-secondary"
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            {showRssForm ? 'Cancel' : '+ Add RSS Feed'}
          </button>
        </div>

        {showRssForm && (
          <form onSubmit={handleAddRssFeed} style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '4px' }}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>RSS Feed URL *</label>
              <input
                type="url"
                placeholder="https://example.com/feed.xml"
                required
                value={newRssUrl}
                onChange={(e) => setNewRssUrl(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>Feed Name (optional)</label>
              <input
                type="text"
                placeholder="Display name for this feed"
                value={newRssFeedName}
                onChange={(e) => setNewRssFeedName(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-success" disabled={addingRss} style={{ padding: '6px 12px', fontSize: '14px' }}>
                {addingRss ? 'Adding...' : 'Add Feed'}
              </button>
              <button type="button" onClick={() => { setShowRssForm(false); setNewRssUrl(''); setNewRssFeedName(''); }} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '14px' }}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {rssFeeds.length > 0 ? (
          <div>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>{rssFeeds.length} RSS feed(s) configured</p>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {rssFeeds.map((feed) => (
                <li key={feed.RSSFeedId} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '10px', 
                  backgroundColor: 'white',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  border: '1px solid #eee'
                }}>
                  <div style={{ flex: 1, wordBreak: 'break-all' }}>
                    <strong style={{ display: 'block', marginBottom: '3px' }}>{feed.FeedName || 'Unnamed Feed'}</strong>
                    <a href={feed.FeedURL} target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4' }}>
                      {feed.FeedURL}
                    </a>
                  </div>
                  <button 
                    onClick={() => setConfirmDeleteRss({ show: true, id: feed.RSSFeedId })}
                    className="btn btn-sm btn-danger"
                    style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p style={{ color: '#999', fontSize: '13px' }}>No RSS feeds configured. Add one to include external news items.</p>
        )}
      </div>

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Edit News Article' : 'New News Article'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title *</label>
              <input
                type="text"
                placeholder="Article Title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Publication Date *</label>
              <input
                type="date"
                required
                value={formData.pubDate}
                onChange={(e) => setFormData({ ...formData, pubDate: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
              <textarea
                placeholder="Article description or summary"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Link</label>
              <input
                type="url"
                placeholder="https://example.com/article"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th>Title</th>
              <th>Publication Date</th>
              <th>Description</th>
              <th>Link</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {news.map((article, index) => {
              const key = article.Source === 'rss' ? `rss-${article.RSSFeedId}-${index}` : article.NewsId;
              const isRss = article.Source === 'rss';
              return (
                <tr 
                  key={key}
                  style={{ backgroundColor: isRss ? '#f5f5f5' : 'white' }}
                >
                  <td style={{ fontWeight: 'bold' }}>
                    {isRss ? (
                      <span style={{ 
                        backgroundColor: '#e3f2fd', 
                        color: '#1976d2', 
                        padding: '3px 8px',
                        borderRadius: '3px',
                        fontSize: '12px'
                      }}>
                        RSS{article.RSSFeedName ? ` - ${article.RSSFeedName}` : ''}
                      </span>
                    ) : (
                      <span style={{ 
                        backgroundColor: '#f0f0f0', 
                        color: '#666', 
                        padding: '3px 8px',
                        borderRadius: '3px',
                        fontSize: '12px'
                      }}>
                        Local
                      </span>
                    )}
                  </td>
                  <td>{article.Title}</td>
                  <td>{new Date(article.PubDate).toLocaleDateString()}</td>
                  <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {article.Description}
                  </td>
                  <td>
                    {article.Link && (
                      <a href={article.Link} target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4' }}>
                        View Article
                      </a>
                    )}
                  </td>
                  <td>
                    {!isRss && (
                      <>
                        <button onClick={() => handleEdit(article.NewsId)} className="btn btn-sm btn-secondary" style={{ marginRight: '5px' }}>
                          Edit
                        </button>
                        <button onClick={() => setConfirmDelete({ show: true, id: article.NewsId })} className="btn btn-sm btn-danger">
                          Delete
                        </button>
                      </>
                    )}
                    {isRss && (
                      <span style={{ color: '#999', fontSize: '12px' }}>External</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {news.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                  No news articles found. Click "Add News Article" to create one or add an RSS feed.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmDelete.show && (
        <ConfirmModal
          show={confirmDelete.show}
          title="Delete News Article"
          message="Are you sure you want to delete this news article? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete({ show: false, id: null })}
        />
      )}

      {confirmDeleteRss.show && (
        <ConfirmModal
          show={confirmDeleteRss.show}
          title="Delete RSS Feed"
          message="Are you sure you want to delete this RSS feed? This action cannot be undone."
          onConfirm={handleDeleteRssFeed}
          onCancel={() => setConfirmDeleteRss({ show: false, id: null })}
        />
      )}
    </div>
  );
};

export default News;
